import log from 'pathfinder/utils/log';
import { createLink } from 'pathfinder/modules/AffiliateUnitModule/helper/affiliateUnitLinkHelper';
import { isLocalHost } from 'utils/env';
import { getHTMLElementCSSProperty, getHTMLElementLineCount } from 'pathfinder/utils/getHTMLElementProperties';
function checkForChildAncestorNodeType(child, stopAtTagName, stopAtElement) {
    let current = child;
    while ((current === null || current === void 0 ? void 0 : current.parentElement) !== null &&
        current.parentElement !== document.documentElement &&
        current.parentElement !== stopAtElement) {
        if (current.parentElement.tagName === stopAtTagName) {
            return true;
        }
        current = current.parentElement;
    }
    return false;
}
function renderUnitMarkup(infoBox, unit, affiliateUnitPageMetadata) {
    var _a;
    const startHeight = getStartHeight(infoBox);
    // only select paragraphs one level from the root main element
    const paragraphs = findParagraphCandidates();
    // don't select placement near images
    const images = findAllImages();
    const notAllowedYStartAndStop = determineInvalidStartingPoints(images);
    // if we cannot find a location after useFallbackAtY use the highest slot below a heading
    let fallbackParagraph;
    const useFallbackAtY = 20000;
    let wentToFallBack = false;
    // prepend the unit after the first paragraph
    let insertionPoint;
    // Must use a regular for loop, instead of a forEach loop.
    // There is no easy to use mechanism (such as break, or returning false) in a forEach loop to break out of the logic, as there is in the jQuery $.each() loop.
    // An exception would need to be thrown in order to exit it, which just adds additional complexity
    // References:
    // https://stackoverflow.com/questions/2641347/short-circuit-array-foreach-like-calling-break#:~:text=There%20is%20no%20way%20to,()%20or%20some()%20instead.
    // https://api.jquery.com/jquery.each/#:~:text=We%20can%20break%20the%20%24.,immediately%20to%20the%20next%20iteration.
    for (let paragraphIndex = 0; paragraphIndex < paragraphs.length; paragraphIndex++) {
        // Grab an easy to use reference to the current paragraph that is being iterated through
        const paragraph = paragraphs[paragraphIndex];
        const paragraphBoundRect = paragraph.getBoundingClientRect();
        const paragraphYStart = paragraphBoundRect.top;
        const paragraphYEnd = paragraphBoundRect.height + paragraphYStart;
        const paragraphYMiddle = paragraphYStart + paragraphBoundRect.height / 2;
        // make sure we are past the infobox and not near an image
        if (paragraphYStart > startHeight &&
            isValidSlot(paragraphYStart, notAllowedYStartAndStop) &&
            isValidSlot(paragraphYEnd, notAllowedYStartAndStop) &&
            isValidSlot(paragraphYMiddle, notAllowedYStartAndStop)) {
            if (!fallbackParagraph) {
                fallbackParagraph = paragraph;
            }
            // when prepending make sure the prev child is a paragraph
            if (((_a = paragraph === null || paragraph === void 0 ? void 0 : paragraph.previousElementSibling) === null || _a === void 0 ? void 0 : _a.tagName) === 'P') {
                // Ensure that we DO NOT try to insert the ad if the parent or one of the ancestor elements is a <table /> element
                if (!checkForChildAncestorNodeType(paragraph, 'TABLE', infoBox)) {
                    insertionPoint = paragraph;
                    break;
                }
            }
        }
        // Once we hit a certain height, go back up and use one of the fallback paragraphs
        if (fallbackParagraph && paragraphYStart > useFallbackAtY) {
            log.debug('AffiliateUnit: inserted using fallback slot.');
            insertionPoint = fallbackParagraph;
            wentToFallBack = true;
            break;
        }
    }
    // If we can't find any paragraph for insertion, and a fallback has already been assigned, then try inserting above one of the first available headers near the mw-content-text id
    const backUpH2Tags = findBackupH2TagCandidate();
    if (backUpH2Tags.length > 0 && wentToFallBack) {
        for (let h2Index = 0; h2Index < backUpH2Tags.length; h2Index++) {
            if (backUpH2Tags[h2Index].getBoundingClientRect().top > startHeight) {
                log.debug('AffiliateUnit: resorted to backup h2 tag for insertion.');
                insertionPoint = backUpH2Tags[h2Index];
                break;
            }
        }
    }
    return insertAtPointAndTrack(insertionPoint, unit, affiliateUnitPageMetadata);
}
/* This helper function fixes a bug on an edge case, where if a preceding figure element that contains a caption is present,
 * and if that caption spans over more than 1 lines, then the affiliate unit ad's image get skewed to the right, due to a margin which bleeds into the affiliate unit's flexbox.
 *
 * As a result, this function is here to ensure:
 * 1. That the preceding element was a <figure>
 * 2. That the preceding element contains a caption which spans over more than 1 lines
 * 3. That if both of the above are true, then the bottom-margin of the <figure> element gets a decreased of about 4px
 */
function adjustMarginOnPrecedingFigureElement(insertionPoint) {
    var _a;
    if (insertionPoint) {
        // Find out if there's a paragraph element, and right before that a figure
        const previousElement = insertionPoint === null || insertionPoint === void 0 ? void 0 : insertionPoint.previousElementSibling;
        if ((previousElement === null || previousElement === void 0 ? void 0 : previousElement.tagName) === 'P') {
            const targetElement = previousElement.previousElementSibling;
            // Found the figure element and one element with a caption tag
            if ((targetElement === null || targetElement === void 0 ? void 0 : targetElement.tagName) === 'FIGURE' && ((_a = targetElement === null || targetElement === void 0 ? void 0 : targetElement.getElementsByClassName('caption')) === null || _a === void 0 ? void 0 : _a.length) === 1) {
                const captionElement = targetElement === null || targetElement === void 0 ? void 0 : targetElement.getElementsByClassName('caption')[0];
                const textLineCount = getHTMLElementLineCount(captionElement);
                if (textLineCount > 1 && getHTMLElementCSSProperty(targetElement, 'margin-bottom').includes('px')) {
                    targetElement.style.setProperty('margin-bottom', '12px');
                }
            }
        }
    }
}
function insertAtPointAndTrack(insertionPoint, unit, affiliateUnitPageMetadata) {
    var _a;
    const isLocal = isLocalHost();
    // Ensure that an insertionPoint was found, if not, then don't bother generating the props for the AffiliateUnitModule
    if (insertionPoint || isLocal) {
        if (isLocal) {
            log.debug('AffiliateUnit: Localhost setup detected. Rendering AffiliateUnit component, even though a proper insertion point may not have been found.');
        }
        log.debug('AffiliateUnit: InsertionPoint', insertionPoint);
        adjustMarginOnPrecedingFigureElement(insertionPoint);
        const isThemeDark = (_a = affiliateUnitPageMetadata.isDarkTheme) !== null && _a !== void 0 ? _a : false;
        // TODO: Need to look into internationalization to generate the disclaimerMessage. unified-platform code had logic in there to generated a message, based on the region. For the time being this is being hard coded.
        // Includes logic for the DDB campaign, and any other possible campaigns that may require a unique, crafted URL
        const updatedLink = createLink(unit, affiliateUnitPageMetadata);
        return {
            insertionPoint: insertionPoint,
            config: {
                image: unit.image,
                heading: unit.heading,
                buttonText: unit.subheading,
                link: updatedLink,
                logoLight: unit.logo && !isThemeDark ? unit.logo.light : null,
                mobileLogoLight: unit.mobileLogo && !isThemeDark ? unit.mobileLogo.light : null,
                logoDark: unit.logo && isThemeDark ? unit.logo.dark : null,
                mobileLogoDark: unit.mobileLogo && !isThemeDark ? unit.mobileLogo.dark : null,
                /* Utilizing extraDisclaimer parameter from the units.ts file, in order to allow dynamic injection of a disclaimer message
           through outside Javascript sources such as GTM */
                disclaimerMessage: unit.extraDisclaimer,
                campaign: unit.campaign,
                category: unit.category,
            },
        };
    }
    else {
        log.debug('AffiliateUnit: A proper insertion point could not be found.');
        return undefined;
    }
}
function findInfoBox() {
    // This is the selector for Gamepedia and Wikia pages on desktop and mobile. It covers any and all infobox elements.
    const infoboxElement = document.querySelector('.mw-parser-output > [class^="infobox"], .mw-parser-output > [class*="infobox"]');
    if (infoboxElement) {
        return infoboxElement;
    }
    return null;
}
function getStartHeight(infoBox) {
    if (!infoBox) {
        return 0;
    }
    const infoBoxBoundingClientRect = infoBox.getBoundingClientRect();
    // TODO: Will need to double check in a prod/dev testing environment if this calculates the correct start height
    const infoBoxHeight = infoBoxBoundingClientRect.height;
    return infoBoxBoundingClientRect.top + infoBoxHeight;
}
function findParagraphCandidates() {
    return document.querySelectorAll('#mw-content-text p');
}
function findBackupH2TagCandidate() {
    return document.querySelectorAll('#mw-content-text h2');
}
function findAllImages() {
    return document.querySelectorAll('#mw-content-text > figure');
}
function determineInvalidStartingPoints(images) {
    const notAllowedYStart = [];
    const notAllowedYStop = [];
    if (images && images.length > 0) {
        images.forEach((element) => {
            const image = element;
            const imageStart = element.getBoundingClientRect().top;
            notAllowedYStart.push(imageStart);
            notAllowedYStop.push(imageStart + image.getBoundingClientRect().height);
        });
    }
    return {
        notAllowedYStart,
        notAllowedYStop,
    };
}
function isValidSlot(yStart, { notAllowedYStart, notAllowedYStop }) {
    // If there are no images, then return true
    if (notAllowedYStart.length === 0) {
        return true;
    }
    // Find the insertion index
    const index = insertSpot(notAllowedYStart, yStart);
    // No images found yet
    if (index === -1) {
        return true;
    }
    const notAllowedYStartValue = notAllowedYStart[index];
    const notAllowedYStopValue = notAllowedYStop[index];
    // Handle case where index is out of bounds, and nothing was returned from the above
    if (notAllowedYStartValue === undefined) {
        return true;
    }
    // verify that the final value that isn't allow is less than the requested y start position
    return yStart < notAllowedYStartValue || yStart > notAllowedYStopValue;
}
// find the first slot where we can insert
function insertSpot(array, value) {
    for (let i = 0; i < array.length - 1; i++) {
        const arrVal = array[i];
        const nextVal = array[i + 1];
        if (i === 0 && arrVal > value) {
            return -1;
        }
        if (arrVal <= value && nextVal > value) {
            return i;
        }
    }
    return array.length - 1;
}
export { findInfoBox, renderUnitMarkup };
